/**
 * CZNA-1466 — "is this the same person?", answered without leaving the form.
 *
 * <p>The namesake hint (CZNA-1034) could only OPEN a candidate, which meant leaving
 * the form she was writing, and the unsaved-changes guard (CZNA-1032) then made her
 * save or discard before she had decided anything. This window puts the record she
 * is writing — unsaved, exactly as it stands — on the left and the candidate on the
 * right, field by field (see {@link compareRecords} for how two different records
 * are paired).
 *
 * <p>Read-only. Nothing is saved and nothing is written into the candidate; closing
 * the window leaves the form untouched. The only way out besides "continue" is to
 * drop the record she is writing, which the host routes through the same path as a
 * confirmed Cancel.
 *
 * <p>CZNA-1577 — the grid itself moved to {@link RecordCompareGrid}, shared with the
 * „Zrušit s náhradou“ page; this window supplies its own words for the two sides.
 */
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Loading, MessageBanner, Modal } from '@folio/stripes/components';
import useRecordDetail from '../../../hooks/useRecordDetail';
import useProposalDetail from '../../../hooks/useProposalDetail';
import { compareRecords } from '../../../utils/compareRecords';
import RecordCompareGrid from '../../RecordCompare';
import { proposalDataToRecordDataFields } from '../utils/proposalDataToRecordDataFields';
/** Left is the record being written, right the candidate. */
var STATUS_MESSAGE_IDS = {
    same: 'ui-czna-authorities.duplicateCompare.status.same',
    different: 'ui-czna-authorities.duplicateCompare.status.different',
    leftOnly: 'ui-czna-authorities.duplicateCompare.status.leftOnly',
    rightOnly: 'ui-czna-authorities.duplicateCompare.status.rightOnly',
    ignored: 'ui-czna-authorities.duplicateCompare.status.ignored',
};
var useCandidateFields = function (candidate) {
    var isProposal = candidate.kind === 'proposal';
    var recordQuery = useRecordDetail(isProposal ? undefined : candidate.id);
    var proposalQuery = useProposalDetail(isProposal ? candidate.id : undefined);
    var fields = useMemo(function () {
        var _a, _b, _c;
        if (isProposal) {
            var proposal = proposalQuery.proposal;
            if (!proposal)
                return undefined;
            return ((_a = proposal.dataFields) === null || _a === void 0 ? void 0 : _a.length)
                ? proposal.dataFields
                : proposalDataToRecordDataFields((_b = proposal.proposal_data) !== null && _b !== void 0 ? _b : []);
        }
        return (_c = recordQuery.record) === null || _c === void 0 ? void 0 : _c.dataFields;
    }, [isProposal, proposalQuery.proposal, recordQuery.record]);
    return {
        fields: fields,
        isLoading: isProposal ? proposalQuery.isLoading : recordQuery.isLoading,
        isError: isProposal ? proposalQuery.isError : recordQuery.isError,
    };
};
var DuplicateCompareModal = function (_a) {
    var candidate = _a.candidate, draftFields = _a.draftFields, onContinue = _a.onContinue, onDiscard = _a.onDiscard;
    var intl = useIntl();
    var _b = useCandidateFields(candidate), fields = _b.fields, isLoading = _b.isLoading, isError = _b.isError;
    var rows = useMemo(function () { return (fields ? compareRecords(draftFields, fields) : undefined); }, [draftFields, fields]);
    var footer = (React.createElement("div", { style: { display: 'flex', justifyContent: 'space-between', gap: '8px' } },
        React.createElement(Button, { buttonStyle: "danger", onClick: onDiscard, "data-testid": "duplicate-compare-discard" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.duplicateCompare.discard" })),
        React.createElement(Button, { buttonStyle: "primary", onClick: onContinue, "data-testid": "duplicate-compare-continue" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.duplicateCompare.continue" }))));
    return (React.createElement(Modal, { open: true, label: intl.formatMessage({ id: 'ui-czna-authorities.duplicateCompare.heading' }), footer: footer, dismissible: true, onClose: onContinue, size: "large", id: "duplicate-compare-modal" },
        React.createElement(RecordCompareGrid, { testIdPrefix: "duplicate-compare", rows: rows, statusMessageIds: STATUS_MESSAGE_IDS, differenceCountMessageId: "ui-czna-authorities.duplicateCompare.differenceCount", leftHeading: React.createElement(FormattedMessage, { id: "ui-czna-authorities.duplicateCompare.left" }), rightHeading: (React.createElement(React.Fragment, null,
                candidate.headingValue,
                candidate.kind === 'proposal' && (React.createElement("span", { style: { fontWeight: 400, color: '#616161', marginLeft: '0.35rem' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.similarHeadings.isProposal" }))))) },
            isLoading && React.createElement(Loading, null),
            isError && (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.duplicateCompare.loadError" }))))));
};
export default DuplicateCompareModal;
